import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth, CONSENT_SKIP_KEY } from '@/contexts/AuthContext';
import { Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { setOverlayBusy } from '@/lib/overlayGate';

const OVERLAY_KEY = 'login-consent-prompt';

// docs/41 P0-1 (founder Q2): on every login, re-ask for any consent the
// account hasn't decided yet. P1 (Care Services, required) BLOCKS the app
// until granted; P2-P4 are asked but individually skippable — DPDPA consent
// must stay freely given, so optional purposes are never forced or
// pre-checked. Save submits ONLY affirmatively-checked purposes: an
// untouched optional is omitted from the payload, stays PENDING server-side
// (POST /consent/grant writes only the purposes present), and is re-asked
// next login — undecided is never converted into a recorded refusal.
//
// "Every login" = each null->user transition (fresh login or app load with a
// token). A skip lasts the tab session; AuthContext clears the skip marker on
// login/register so the next sign-in re-asks. WITHDRAWN counts as decided —
// an explicit "no" is respected, not nagged.

const PURPOSE_FALLBACKS = {
  P1: { title: 'Care Services', hint: 'Required to deliver care: medications, carer visits, reports, and AI summaries for your family member.' },
  P2: { title: 'Health Passport & Longitudinal Record', hint: 'Builds a long-term health record for trends and proactive alerts.' },
  P3: { title: 'Monthly AI Health Report', hint: 'A monthly AI-generated summary of your family member’s health.' },
  P4: { title: 'Help Improve Care for All Families', hint: 'Contribute anonymised data to improve care quality. Never identifies your family.' },
};

export function LoginConsentPrompt() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // True once the lookup below has said whether the prompt opens.
  const [decided, setDecided] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [members, setMembers] = useState([]); // [{id, name, missing: [pid], purposes: {pid: status}}]
  const [visiblePurposes, setVisiblePurposes] = useState(['P1', 'P2', 'P3', 'P4']);
  const [choices, setChoices] = useState({}); // {beneficiaryId: {P1: true, ...}}
  const [textVersion, setTextVersion] = useState('v1.0');
  const [purposeTitles, setPurposeTitles] = useState({});
  const [legalAuthority, setLegalAuthority] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const checkedForUser = useRef(null);

  useEffect(() => {
    if (!user?.id || checkedForUser.current === user.id) return;
    checkedForUser.current = user.id;

    const evaluate = async () => {
      try {
        const [statusRes, textsRes, settingsRes, membersRes] = await Promise.all([
          api.get(`/consent/status/${user.id}`),
          api.get('/consent/texts').catch(() => ({ data: [] })),
          api.get('/consent-settings').catch(() => ({ data: [] })),
          api.get('/family-members').catch(() => ({ data: [] })),
        ]);

        const beneficiaries = statusRes.data?.beneficiaries || {};
        const texts = textsRes.data || [];
        const settings = settingsRes.data || [];
        const familyMembers = Array.isArray(membersRes.data) ? membersRes.data : [];
        if (familyMembers.length === 0) return;

        // FAMILY-scoped purposes only. P5 (WhatsApp marketing) and P6 (tax
        // document AI) are account/self-scoped with their own grant surfaces —
        // if they leak into this prompt's pending math they become UNDECIDABLE
        // (the dialog renders P1–P4 checkboxes only) and the popup re-opens on
        // every navigation forever (prod regression 2026-07-22: seeding the
        // visible P6 consent-settings row did exactly that).
        const FAMILY_PURPOSES = ['P1', 'P2', 'P3', 'P4'];
        const visible = (settings.length > 0
          ? settings.filter(s => s.visible).map(s => s.purpose_id)
          : FAMILY_PURPOSES
        ).filter(pid => FAMILY_PURPOSES.includes(pid));

        // /consent/texts serves { version_id: "v1.0-P1", heading_text, purpose_id }
        // — NOT { version, title } (review 2026-07-14: reading the wrong fields
        // silently froze the grant's version stamp at the 'v1.0' default, so
        // once texts rev past v1.0 every login-prompt grant recorded a stale
        // DPDPA text version). The grant handler rebuilds `{base}-{purpose_id}`,
        // so we send the BASE version (version_id with the -P{n} suffix stripped).
        let latestVersion = 'v1.0';
        const titles = {};
        for (const t of texts) {
          const base = (t.version_id || '').replace(/-P\d+$/, '');
          if (base && base > latestVersion) latestVersion = base;
          if (t.purpose_id && t.heading_text && !titles[t.purpose_id]) {
            // Drop the trailing "(Required)"/"(Optional)" — the UI adds its own badge.
            titles[t.purpose_id] = t.heading_text.replace(/\s*\((Required|Optional)\)\s*$/i, '');
          }
        }

        const rows = [];
        const prefill = {};
        let anyP1Missing = false;
        for (const fm of familyMembers) {
          const purposes = beneficiaries[fm.id] || {};
          const missing = visible.filter(pid => {
            const c = purposes[pid];
            return !c || c.status === 'PENDING'; // WITHDRAWN = decided
          });
          if (missing.length === 0) continue;
          if (missing.includes('P1')) anyP1Missing = true;
          prefill[fm.id] = {};
          for (const pid of ['P1', 'P2', 'P3', 'P4']) {
            // Pre-fill true = will be (re)granted on save; false = undecided,
            // omitted from the payload entirely (stays PENDING server-side).
            prefill[fm.id][pid] = pid === 'P1' ? true : purposes[pid]?.status === 'GRANTED';
          }
          rows.push({ id: fm.id, name: fm.name || 'Family member', missing, purposes });
        }

        if (rows.length === 0) return;
        if (!anyP1Missing && sessionStorage.getItem(CONSENT_SKIP_KEY) === user.id) return;

        setMembers(rows);
        setChoices(prefill);
        setVisiblePurposes(visible);
        setTextVersion(latestVersion);
        setPurposeTitles(titles);
        setBlocking(anyP1Missing);
        setOpen(true);
      } catch {
        // Never block the app on a failed consent lookup.
      }
    };

    evaluate().finally(() => setDecided(true));
  }, [api, user]);

  // This prompt opens by itself, a moment after the page (it looks four
  // things up first). Anything else that opens by itself has to wait until
  // it has decided and, if it opened, until it is closed: the dashboard tour
  // would otherwise appear and be covered a second later.
  useEffect(() => {
    setOverlayBusy(OVERLAY_KEY, !decided || open);
    return () => setOverlayBusy(OVERLAY_KEY, false);
  }, [decided, open]);

  useEffect(() => {
    if (open && dialogRef.current) dialogRef.current.focus();
  }, [open]);

  const skip = useCallback(() => {
    if (blocking) return;
    sessionStorage.setItem(CONSENT_SKIP_KEY, user?.id || '1');
    setOpen(false);
  }, [blocking, user]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') skip(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, skip]);

  if (!open) return null;

  const toggle = (bid, pid) => {
    if (pid === 'P1') return;
    setChoices(prev => ({ ...prev, [bid]: { ...prev[bid], [pid]: !prev[bid]?.[pid] } }));
  };

  const save = async () => {
    if (!legalAuthority) {
      setError('Please confirm you have the authority to consent on behalf of your family member(s).');
      return;
    }
    setSaving(true);
    setError('');
    try {
      for (const m of members) {
        // Only affirmative choices travel. Unchecked here means "still
        // undecided" (decided rows render disabled), so omitting them keeps
        // them PENDING server-side and they are re-asked next login —
        // never silently written as WITHDRAWN (review 2026-07-14).
        const decided = Object.fromEntries(
          Object.entries(choices[m.id] || {}).filter(([, v]) => v === true)
        );
        await api.post('/consent/grant', {
          beneficiary_id: m.id,
          consents: decided,
          legal_authority_confirmed: legalAuthority,
          ip_address: '0.0.0.0',
          user_agent: navigator.userAgent,
          method: 'LOGIN_PROMPT',
          consent_text_version: textVersion,
        });
      }
      window.dispatchEvent(new Event('consents-updated'));
      setOpen(false);
    } catch {
      setError('Could not save your consent choices. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const titleFor = (pid) => purposeTitles[pid] || PURPOSE_FALLBACKS[pid]?.title || pid;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
      onMouseDown={(e) => { if (e.target === e.currentTarget) skip(); }}>
      <div ref={dialogRef} tabIndex={-1} role="dialog" aria-modal="true"
        aria-labelledby="login-consent-title" data-testid="login-consent-prompt"
        className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl border bg-card text-card-foreground shadow-xl outline-none">
        <div className="p-5 border-b flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 id="login-consent-title" className="text-lg font-semibold">Your consent choices</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {blocking
                ? 'Care Services consent is required before nsofar can manage care for your family.'
                : 'A few optional consent choices are still undecided. You can skip and decide later.'}
            </p>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {members.map(m => (
            <fieldset key={m.id} className="rounded-xl border p-4">
              <legend className="px-1 text-sm font-semibold">{m.name}</legend>
              <div className="space-y-3 mt-1">
                {['P1', 'P2', 'P3', 'P4'].filter(pid => visiblePurposes.includes(pid)).map(pid => {
                  const decided = !m.missing.includes(pid);
                  const inputId = `consent-${m.id}-${pid}`;
                  return (
                    <div key={pid} className={`flex items-start gap-3 ${decided ? 'opacity-60' : ''}`}>
                      <input
                        id={inputId}
                        type="checkbox"
                        className="mt-1 h-4 w-4 flex-shrink-0 accent-primary"
                        checked={!!choices[m.id]?.[pid]}
                        disabled={pid === 'P1' || decided || saving}
                        onChange={() => toggle(m.id, pid)}
                        data-testid={inputId}
                      />
                      <label htmlFor={inputId} className="text-sm cursor-pointer">
                        <span className="font-medium">
                          {titleFor(pid)}
                          {pid === 'P1' && <span className="ml-1.5 text-xs font-semibold text-primary">Required</span>}
                          {decided && <span className="ml-1.5 text-xs text-muted-foreground">already decided</span>}
                        </span>
                        <span className="block text-xs text-muted-foreground mt-0.5">
                          {PURPOSE_FALLBACKS[pid]?.hint}
                        </span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </fieldset>
          ))}

          <div className="flex items-start gap-3">
            <input
              id="consent-legal-authority"
              type="checkbox"
              className="mt-1 h-4 w-4 flex-shrink-0 accent-primary"
              checked={legalAuthority}
              disabled={saving}
              onChange={() => setLegalAuthority(v => !v)}
              data-testid="consent-legal-authority"
            />
            <label htmlFor="consent-legal-authority" className="text-sm cursor-pointer">
              I confirm I have the authority to make these choices on behalf of my family member(s).
            </label>
          </div>

          <p className="text-xs text-muted-foreground">
            Full consent texts and withdrawal controls are in{' '}
            <button type="button" className="underline underline-offset-2"
              onClick={() => { setOpen(false); navigate('/settings/privacy'); }}>
              Privacy Settings
            </button>. You can change optional choices any time.
          </p>

          {error && (
            <p role="alert" className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        <div className="p-5 pt-0 flex items-center justify-end gap-3">
          {!blocking && (
            <button type="button" data-testid="consent-login-skip" onClick={skip} disabled={saving}
              className="px-4 py-2 text-sm font-medium rounded-lg border hover:bg-accent">
              Not now
            </button>
          )}
          <button type="button" data-testid="consent-login-save" onClick={save} disabled={saving}
            className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save choices'}
          </button>
        </div>
      </div>
    </div>
  );
}
